import { generateTags } from "../estada/C1_generateTags";
import type { C1_EloItem_Data } from "../types/C1_EloTypes";
import { C1_Furniture } from "./C1_Furniture";
import C1_EloItem from "./C1_EloItem";
import { C1_Consumer } from "./C1_Consumer";
import { C1_EloGroup } from "./C1_EloGroup";
import { C1_Opening } from "./C1_Opening";
import { C1_Room } from "./C1_Room";
import { c1_state } from "./C1_runtimeContext";
import type Group from "../../../src/models/Group";
import type Room from "../../../src/models/Room";
import { getMaterializedEloRoot } from "./C1_materializeEloDefinition";
import type { Rect } from "../types/C1_AppTypes";

export type C1_EloOwner = C1_Room | C1_Opening | C1_Furniture;

/** Resolves the current root facade from an owner's ESTADA child collections. */
export function materializedRootForEloOwner(owner: C1_EloOwner): C1_EloGroup | undefined {
  const source =
    owner instanceof C1_Room
      ? owner.eloTree
      : owner instanceof C1_Opening
        ? owner.eloTree
        : owner.eloTree;
  return getMaterializedEloRoot(owner.owner as Room | Group | undefined, source);
}

export function eloRootAndOwner(item: C1_EloItem): { root: C1_EloGroup; owner: C1_EloOwner } | undefined {
  let estadaOwner = item.owner;
  while (estadaOwner) {
    const facet = estadaOwner.c1;
    if (facet instanceof C1_Consumer && facet.data.lightGroupFurniture) {
      const owner = facet.lightGroup;
      if (!owner) return undefined;
      const root = materializedRootForEloOwner(owner);
      return root ? { root, owner } : undefined;
    }
    if (facet instanceof C1_Room || facet instanceof C1_Opening || facet instanceof C1_Furniture) {
      const root = materializedRootForEloOwner(facet);
      return root ? { root, owner: facet } : undefined;
    }
    estadaOwner = estadaOwner.parent;
  }
  return undefined;
}

/** Evaluates one ELO item hierarchy with the same owner geometry used by the plan renderer. */
export function eloItemPlanBounds(item: C1_EloItem): Rect | undefined {
  const resolved = eloRootAndOwner(item);
  if (!resolved) return undefined;
  const { root, owner } = resolved;
  let origin: { x: number; y: number } | undefined;
  let rotation = 0;
  let reflect = false;
  const placementProps: Record<string, number> = {};

  if (owner instanceof C1_Room) {
    const rect = owner.rect();
    origin = owner.automaticPlacementPoint();
    placementProps.SX = placementProps.sx = rect.x2 - rect.x1;
    placementProps.SY = placementProps.sy = rect.y2 - rect.y1;
  } else if (owner instanceof C1_Furniture) {
    origin = owner.equipmentPostitionGeometric(owner.id);
    rotation = owner.absoluteRotation;
    reflect = owner.absoluteReflect;
    for (const [key, value] of Object.entries(owner.baseProps)) {
      if (typeof value === "number") placementProps[key] = value;
    }
    placementProps.SX = placementProps.sx = owner.baseProps.width;
    placementProps.SY = placementProps.sy = owner.baseProps.height;
  } else if (owner instanceof C1_Opening) {
    origin = owner.absolutePosition;
    rotation = { top: 0, right: 90, bottom: 180, left: 270 }[owner.side];
    reflect = owner.kind === "door" && owner.reflect === true;
    const room = owner.parent as C1_Room | undefined;
    const wallThickness = (owner.wall && room ? room.isExteriorSegment(owner.wall) : true)
      ? c1_state.project.settings.wallThicknessExterior
      : c1_state.project.settings.wallThicknessInterior;
    placementProps.SX = owner.width;
    placementProps.WT = wallThickness;
    placementProps.DStation = 0.3;
    placementProps.DButton = 0.1;
    placementProps.DPlug = 0.1;
    placementProps.DLight = 0.3;
    placementProps.DPaper = 0.5;
  }
  if (!origin) return undefined;

  root.evaluateAbsolutePlacements(origin, rotation, reflect, placementProps);
  const target = root.findItem(item.id);
  if (!target) return undefined;
  const items = target instanceof C1_EloGroup ? target.allItems : [target];
  const bounds: Rect = { x1: Infinity, y1: Infinity, x2: -Infinity, y2: -Infinity };
  for (const candidate of items) {
    const position = candidate.evaluatedAbsolutePosition;
    if (!position) continue;
    bounds.x1 = Math.min(bounds.x1, position.x);
    bounds.y1 = Math.min(bounds.y1, position.y);
    bounds.x2 = Math.max(bounds.x2, position.x);
    bounds.y2 = Math.max(bounds.y2, position.y);
  }
  return Number.isFinite(bounds.x1) ? bounds : undefined;
}

export function commitOwnerEloTree(
  owner: C1_EloOwner,
  nextRoot: C1_EloItem_Data,
  selectedItemId?: string,
  finishInteraction = true,
  preserveSelection = false,
): void {
  if (owner instanceof C1_Room) owner.setEloTree(nextRoot);
  else if (owner instanceof C1_Opening) owner.setEloTree(nextRoot);
  else owner.setEloTree(nextRoot);

  const nextModelRoot = materializedRootForEloOwner(owner);
  if (!preserveSelection) c1_state.setSelection(selectedItemId ? (nextModelRoot?.findItem(selectedItemId) ?? owner) : owner);
  generateTags(c1_state.project);
  if (finishInteraction) queueMicrotask(() => state.undoManager.userInteractionEnded("Elektroausstattung ändern"));
}
